import React, { useCallback, useContext } from "react"
import { useStateMachine } from "../../../context/stateMachine/StateMachineV2"
import { defineMessage, useIntl } from "react-intl"
import { CarrotCardCostSharePreAuthContext } from "../../state/CarrotCardCostSharePreAuthContext"
import { useConvertTranslatedLabel } from "#/services/common-forms/useConvertTranslatedLabel"
import { FormSelectOption } from "@carrotfertility/carotene-core-x"
import { RelatedToStorageFeesValues } from "#/components/carrot-card-cost-share/steps/shared/stepOptionValues"
import { JourneyTypes } from "#/components/get-reimbursed/steps/ExpenseJourneyTypes"
import { RadioGroupWithTileStep } from "#/components/carrot-card-cost-share/steps/variants/RadioGroupWithTileStep"
import { ThisIsADifferentExpenseTypeBox } from "#/components/carrot-card-cost-share/steps/shared/ThisIsADifferentExpenseTypeBox"

const storageFeesOptions = [
  {
    label: defineMessage({ defaultMessage: "No" }),
    value: RelatedToStorageFeesValues.OTHER
  },
  {
    label: defineMessage({ defaultMessage: "Yes: First year of storage" }),
    value: RelatedToStorageFeesValues.FIRST_YEAR_STORAGE
  },
  {
    label: defineMessage({ defaultMessage: "Yes: After first year of storage" }),
    value: RelatedToStorageFeesValues.POST_FIRST_YEAR_STORAGE
  }
]

export function RelatedToStorageFeesStep(): JSX.Element {
  const intl = useIntl()
  const { send } = useStateMachine()
  const {
    dispatchFormState,
    formState: { isStorageFees, journeyType },
    serverState: { hasElectivePreservationCoverage }
  } = useContext(CarrotCardCostSharePreAuthContext)

  const formattedOptions = useConvertTranslatedLabel(storageFeesOptions) as FormSelectOption[]

  const onSubmit = useCallback(
    (formValues: { [key: string]: string }): void => {
      dispatchFormState({ type: "UPDATE_FIELD", field: "isStorageFees", value: formValues.isStorageFees })
      if (
        [JourneyTypes.PRESERVATION, JourneyTypes.FERTILITY_CARE].includes(journeyType as JourneyTypes) &&
        formValues.isStorageFees === RelatedToStorageFeesValues.POST_FIRST_YEAR_STORAGE &&
        !hasElectivePreservationCoverage
      ) {
        send("NO_ELECTIVE_PRES_COVERAGE")
        return
      }
      if (
        formValues.isStorageFees === RelatedToStorageFeesValues.POST_FIRST_YEAR_STORAGE &&
        hasElectivePreservationCoverage &&
        journeyType === JourneyTypes.FERTILITY_CARE
      ) {
        dispatchFormState({ type: "UPDATE_FIELD", field: "journeyType", value: JourneyTypes.PRESERVATION })
      }
      send(formValues.isStorageFees)
    },
    [dispatchFormState, send, hasElectivePreservationCoverage, journeyType]
  )

  return (
    <RadioGroupWithTileStep
      title={intl.formatMessage({ defaultMessage: "Is this related to storage fees?" })}
      description={intl.formatMessage({
        defaultMessage: "This helps us determine if the care may be a qualified medical expense."
      })}
      name="isStorageFees"
      options={formattedOptions}
      onSubmit={onSubmit}
      defaultValue={isStorageFees}
    >
      <ThisIsADifferentExpenseTypeBox name="isStorageFees" />
    </RadioGroupWithTileStep>
  )
}
